import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './context/AuthContext';
import PrivateRoute from './components/Auth/PrivateRoute';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import AreaOptions from './pages/AreaOptions';
import StudyTopics from './pages/StudyTopics';
import StudyModule from './pages/StudyModule';
import ExamConfig from './pages/ExamConfig';
import ExamPage from './pages/ExamPage';
import SimulacroPage from './pages/SimulacroPage';
import RankingPage from './pages/RankingPage';
import HistorialPage from './pages/HistorialPage';
import TeacherDashboard from './pages/TeacherDashboard';
import AdminDashboard from './pages/AdminDashboard';
import CreateQuestion from './pages/CreateQuestion';
import ExamBrowser from './pages/ExamBrowser';
import TakeSpecificExam from './pages/TakeSpecificExam';
import QuestionForm from './pages/teacher/QuestionForm';
import QuestionsList from './pages/teacher/QuestionsList';
import QuestionSets from './pages/teacher/QuestionSets';
import ExamCreator from './pages/teacher/ExamCreator';
import ExamsList from './pages/teacher/ExamsList';

// Theme configuration
const theme = createTheme({
  palette: {
    primary: {
      main: '#2196f3',
      dark: '#1976d2',
      light: '#64b5f6',
    },
    secondary: {
      main: '#ff9800',
      dark: '#f57c00',
      light: '#ffb74d',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            
            {/* Student Routes */}
            <Route path="/dashboard" element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            } />
            <Route path="/area/:area" element={
              <PrivateRoute>
                <AreaOptions />
              </PrivateRoute>
            } />
            <Route path="/study/:area/topics" element={
              <PrivateRoute>
                <StudyTopics />
              </PrivateRoute>
            } />
            <Route path="/study/:area/content" element={
              <PrivateRoute>
                <StudyModule />
              </PrivateRoute>
            } />
            <Route path="/exam/:area/config" element={
              <PrivateRoute>
                <ExamConfig />
              </PrivateRoute>
            } />
            <Route path="/exam/:area" element={
              <PrivateRoute>
                <ExamPage />
              </PrivateRoute>
            } />
            <Route path="/simulacro" element={
              <PrivateRoute>
                <SimulacroPage />
              </PrivateRoute>
            } />
            <Route path="/ranking" element={
              <PrivateRoute>
                <RankingPage />
              </PrivateRoute>
            } />
            <Route path="/historial" element={
              <PrivateRoute>
                <HistorialPage />
              </PrivateRoute>
            } />
            <Route path="/exams" element={
              <PrivateRoute>
                <ExamBrowser />
              </PrivateRoute>
            } />
            <Route path="/exam/specific/:examId" element={
              <PrivateRoute>
                <TakeSpecificExam />
              </PrivateRoute>
            } />
            
            {/* Teacher Routes */}
            <Route path="/teacher" element={
              <PrivateRoute requiredRole="teacher">
                <TeacherDashboard />
              </PrivateRoute>
            } />
            <Route path="/teacher/dashboard" element={
              <PrivateRoute requiredRole="teacher">
                <TeacherDashboard />
              </PrivateRoute>
            } />
            <Route path="/teacher/create-question" element={
              <PrivateRoute requiredRole="teacher">
                <CreateQuestion />
              </PrivateRoute>
            } />
            <Route path="/teacher/questions" element={
              <PrivateRoute requiredRole="teacher">
                <QuestionsList />
              </PrivateRoute>
            } />
            <Route path="/teacher/question-sets" element={
              <PrivateRoute requiredRole="teacher">
                <QuestionSets />
              </PrivateRoute>
            } />
            <Route path="/teacher/questions/new" element={
              <PrivateRoute requiredRole="teacher">
                <QuestionForm />
              </PrivateRoute>
            } />
            <Route path="/teacher/questions/edit/:id" element={
              <PrivateRoute requiredRole="teacher">
                <QuestionForm />
              </PrivateRoute>
            } />
            <Route path="/teacher/exams/create" element={
              <PrivateRoute requiredRole="teacher">
                <ExamCreator />
              </PrivateRoute>
            } />
            <Route path="/teacher/exams" element={
              <PrivateRoute requiredRole="teacher">
                <ExamsList />
              </PrivateRoute>
            } />
            <Route path="/teacher/exams/edit/:id" element={
              <PrivateRoute requiredRole="teacher">
                <ExamCreator />
              </PrivateRoute>
            } />
            <Route path="/teacher/exams/view/:id" element={
              <PrivateRoute requiredRole="teacher">
                <ExamsList />
              </PrivateRoute>
            } />
            
            {/* Admin Routes */}
            <Route path="/admin" element={
              <PrivateRoute requiredRole="admin">
                <AdminDashboard />
              </PrivateRoute>
            } />
            
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
